import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { tokens } from '../../theme';
import { Button } from '../ui';

export type GuestBannerState = 'fresh' | 'warn' | 'expire';

export interface GuestBannerProps {
  state?: GuestBannerState;
  daysLabel?: string;
  usedMB: number;
  totalMB?: number;
  onSave?: () => void;
  saveLabel?: string;
}

const palette = (state: GuestBannerState) => {
  if (state === 'warn') {
    return {
      bg: 'rgba(227,165,73,0.13)',
      border: 'rgba(227,165,73,0.5)',
      icBg: 'rgba(227,165,73,0.2)',
      ic: tokens.color.warn,
      barFill: tokens.color.warn,
    };
  }
  if (state === 'expire') {
    return {
      bg: 'rgba(229,87,92,0.13)',
      border: 'rgba(229,87,92,0.5)',
      icBg: 'rgba(229,87,92,0.2)',
      ic: tokens.color.danger,
      barFill: tokens.color.danger,
    };
  }
  return {
    bg: 'rgba(39,182,203,0.12)',
    border: 'rgba(39,182,203,0.42)',
    icBg: 'rgba(39,182,203,0.18)',
    ic: tokens.color.accent,
    barFill: tokens.color.accent,
  };
};

const GuestBanner: React.FC<GuestBannerProps> = ({
  state = 'fresh',
  daysLabel,
  usedMB,
  totalMB = 100,
  onSave,
  saveLabel = 'Save your work',
}) => {
  const p = palette(state);
  const pct = Math.min(100, Math.max(0, Math.round((usedMB / totalMB) * 100)));
  const defaultDays =
    state === 'expire' ? 'Ends today' : state === 'warn' ? '2 days left' : '5 days left';

  return (
    <div
      role="status"
      aria-live="polite"
      className="m-3 flex flex-col gap-3 rounded-[12px] p-4 font-sans text-[14px] text-body"
      style={{ background: p.bg, border: `1px solid ${p.border}` }}
    >
      <div className="flex items-center gap-[10px]">
        <div
          className="grid h-8 w-8 place-items-center rounded-[8px]"
          style={{ background: p.icBg, color: p.ic }}
        >
          {state === 'fresh' ? (
            <AccessTimeIcon style={{ fontSize: 20 }} />
          ) : (
            <ErrorOutlineIcon style={{ fontSize: 20 }} />
          )}
        </div>
        <div className="flex flex-col">
          <b className="font-bold text-white">Guest session</b>
          <div className="text-[13px] opacity-80">{daysLabel ?? defaultDays}</div>
        </div>
      </div>

      <div className="mt-1 flex flex-col gap-[6px]">
        <div className="flex justify-between text-[12px] opacity-80">
          <span>{usedMB} MB</span>
          <span>{totalMB} MB</span>
        </div>
        <div
          aria-hidden
          className="h-[6px] w-full overflow-hidden rounded-[4px] bg-[rgba(255,255,255,0.12)]"
        >
          <div
            className="h-full rounded-[4px]"
            style={{ width: `${pct}%`, background: p.barFill }}
          />
        </div>
      </div>

      <div className="mt-2 w-full [&_button]:w-full">
        <Button variant="primary" size="md" onClick={onSave}>
          {saveLabel}
        </Button>
      </div>
    </div>
  );
};

export default GuestBanner;
